import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getToolPage, TOOL_PAGES } from '../data/toolPages';
import { CheckCircle, ArrowRight } from '@phosphor-icons/react';

export default function ToolPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const tool = getToolPage(slug);

  useEffect(() => {
    if (!tool) return;
    window.scrollTo(0, 0);
    setPageMeta({
      title: tool.metaTitle,
      description: tool.metaDescription,
      url: window.location.href,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: tool.faqs.map((f) => ({
            '@type': 'Question', name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        breadcrumbLd([
          { name: 'Home', url: window.location.origin },
          { name: 'Features', url: `${window.location.origin}/home` },
          { name: tool.h1 },
        ]),
      ],
    });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!tool) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl mb-4">Tool not found</p>
          <Link to="/home" className="text-[#D95800] font-bold underline">See all features</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid={`tool-page-${tool.slug}`}>
      {/* Hero */}
      <div className="bg-neutral-900 border-b-4 border-[#D95800] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-black uppercase tracking-wider text-[#D95800] mb-3">Free ConstructHelp Tool</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white uppercase leading-tight mb-4">{tool.h1}</h1>
          <p className="text-lg text-neutral-300 max-w-2xl mb-8">{tool.subtitle}</p>
          <button onClick={() => navigate('/register')}
            className="h-14 px-8 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase tracking-wider border-2 border-black shadow-[4px_4px_0px_#000] flex items-center gap-2"
            data-testid="tool-cta-hero">
            {tool.ctaLabel} <ArrowRight size={20} weight="bold" />
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">
        <p className="text-lg text-neutral-800 dark:text-neutral-200 leading-relaxed">{tool.intro}</p>

        {/* Product screenshot */}
        <div data-testid="tool-screenshot">
          <div className="border-4 border-black shadow-[8px_8px_0px_#D95800] overflow-hidden bg-white">
            <div className="bg-neutral-900 px-4 py-2 flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-red-500" />
              <span className="w-3 h-3 rounded-full bg-yellow-400" />
              <span className="w-3 h-3 rounded-full bg-emerald-500" />
              <span className="text-neutral-400 text-xs font-bold ml-3">constructhelp.com</span>
            </div>
            <img src={`/tool-previews/${tool.slug}.jpg`} alt={`${tool.h1} — real product screenshot`}
              className="w-full" loading="lazy" data-testid={`tool-screenshot-img-${tool.slug}`} />
          </div>
          <p className="text-xs font-bold text-neutral-500 mt-2 text-center">Actual product — this is what you get when you sign up.</p>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tool-features">
          {tool.features.map((f, i) => (
            <div key={i} className="bg-white dark:bg-neutral-800 border-2 border-black p-5 shadow-[4px_4px_0px_#000]">
              <p className="font-black text-neutral-900 dark:text-white mb-1 flex items-center gap-2">
                <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" /> {f.title}
              </p>
              <p className="text-sm text-neutral-600 dark:text-neutral-300">{f.text}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div data-testid="tool-steps">
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">How it works</h2>
          <div className="space-y-3">
            {tool.steps.map((s, i) => (
              <div key={i} className="flex items-center gap-4 bg-white dark:bg-neutral-800 border-2 border-neutral-900 p-4">
                <span className="w-9 h-9 shrink-0 bg-[#D95800] text-white font-black flex items-center justify-center border-2 border-black">{i + 1}</span>
                <p className="font-bold text-sm text-neutral-800 dark:text-neutral-200">{s}</p>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs */}
        <div data-testid="tool-faqs">
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Frequently asked questions</h2>
          <div className="space-y-3">
            {tool.faqs.map((f, i) => (
              <div key={i} className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 p-4">
                <p className="font-black text-sm text-neutral-900 dark:text-white mb-1">{f.q}</p>
                <p className="text-sm text-neutral-600 dark:text-neutral-300">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Related + CTA */}
        <div className="flex flex-wrap gap-2" data-testid="tool-related">
          {tool.related.map((r) => {
            const t = TOOL_PAGES.find((x) => x.slug === r);
            return t ? (
              <Link key={r} to={`/tools/${r}`} className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50">
                {t.h1} →
              </Link>
            ) : null;
          })}
          <Link to="/home" className="px-3 py-2 bg-neutral-900 text-white border-2 border-neutral-900 text-sm font-bold hover:bg-neutral-700" data-testid="tool-all-features-link">
            All features →
          </Link>
        </div>

        <div className="bg-neutral-900 border-2 border-black p-8 text-center shadow-[6px_6px_0px_#D95800]">
          <h2 className="text-xl font-black text-white uppercase mb-2">Free to start. No credit card.</h2>
          <p className="text-neutral-400 text-sm mb-5">Join thousands of pros and homeowners building smarter on ConstructHelp.</p>
          <button onClick={() => navigate('/register')}
            className="h-12 px-8 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-black shadow-[3px_3px_0px_#000]"
            data-testid="tool-cta-bottom">
            {tool.ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
